'use client';
/**
 * ANNUAIRE — briques communes aux écrans (lot 2, étape 3).
 *
 * L'écran reflète les règles, le moteur les applique : chaque saisie est
 * revérifiée par `POST ged/annuaire/verifier` (sans écriture) et par la route
 * d'écriture elle-même. Rien ici ne décide qu'une valeur est bonne.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGed } from '@/lib/store';
import { estConvention } from '@/lib/annuaire-conventions';
import { fetchEcriture } from '@/lib/ecrire';
import { I, inputStyle, Selecteur, Pastille, Modale } from '@/components/ui';
import { lbl } from '../ressources/commun';
import { Bouton } from '@/shared/Bouton';
import { INDICATIFS_TEL } from '@/shared/ChampTelephone';
import { PiedActions } from '@/shared/PiedActions';
import { typeDeduitTelephone, LIBELLE_TYPE_TELEPHONE } from '@/lib/annuaire';

/* ── Types servis par le moteur ─────────────────────────────────────────── */

export type Entree = { code: string; libelle: string; synonymes?: string[]; actif?: boolean; critique?: boolean };
export type Referentiels = {
  specialites: Entree[]; categoriesAchat: Entree[]; fonctions: Entree[]; libellesService: Entree[]; villes: Entree[]; typesClause: Entree[];
  moi: { ecrire_correspondant: boolean; ecrire_fournisseur: boolean; desactiver: boolean; fusionner: boolean; changer_type: boolean; referentiels: boolean; permissions: boolean; nom: string;
    conventions_lire: boolean; conventions_rediger: boolean; conventions_valider: boolean; voir_prix: boolean };
};
export type Defaut = { champ: string; code: string; message: string; valeur?: string; exemple?: string; derogeable?: boolean };
export type Verdict = { erreurs: Defaut[]; manques: Defaut[]; avertissements: Defaut[]; propositions: Defaut[] };
export type Candidat = { id: string; libelle: string; entite: string; score: number; bloquant: boolean; motifs: { code: string; libelle: string }[]; proposerAffiliation?: boolean };
export type Tel = { id?: number; indicatif: string; numero_national?: string; numero: string; affichage?: string; type: string; principal: boolean; actif?: boolean };
export type Mail = { id?: number; adresse: string; usages?: string[]; principal: boolean; actif?: boolean };

/* ── Accès au moteur ────────────────────────────────────────────────────── */

export class ErreurAnnuaire extends Error {
  details: Record<string, unknown> | undefined;
  statut: number;
  constructor(message: string, statut: number, details?: Record<string, unknown>) { super(message); this.statut = statut; this.details = details; }
}

/** Lecture. Un échec se dit : jamais une liste vide à la place d'une erreur. */
export async function lire<T>(chemin: string): Promise<T> {
  const r = await fetch(`/api/ged/${chemin}`, { credentials: 'include' });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new ErreurAnnuaire(j?.error?.message || 'Lecture impossible.', r.status, j?.error?.details);
  return j as T;
}

/** Écriture (témoin « l'application travaille » allumé). Le refus garde ses détails : verdict, doublons. */
export async function ecrire<T = Record<string, unknown>>(chemin: string, corps: unknown): Promise<T> {
  const r = await fetchEcriture(`/api/ged/${chemin}`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps ?? {}),
  });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new ErreurAnnuaire(j?.error?.message || 'Enregistrement refusé.', r.status, j?.error?.details);
  return j as T;
}

/** Ouvrir une référence où elle vit : une convention dans l'écran Conventions, une fiche dans l'annuaire
 *  (les conventions sont une entité à part depuis le 21/10/2026 ; une fiche contact garde la liste des siennes). */
export function useOuvrirReference() {
  const { naviguerVers } = useGed();
  return useCallback((id: string) => (estConvention(id)
    ? naviguerVers('conventions', { cle: 'selectedConvention', valeur: id })
    : naviguerVers('annuaire', { cle: 'selectedAnnuaire', valeur: id })), [naviguerVers]);
}

/** Les référentiels, chargés une fois par écran. */
export function useReferentiels() {
  const [refs, setRefs] = useState<Referentiels | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tour, setTour] = useState(0);
  useEffect(() => {
    let vivant = true;
    lire<Referentiels>('ged/annuaire/referentiels').then((r) => { if (vivant) { setRefs(r); setErreur(null); } })
      .catch((e: Error) => { if (vivant) setErreur(e.message); });
    return () => { vivant = false; };
  }, [tour]);
  return { refs, erreur, recharger: () => setTour((t) => t + 1) };
}

export const libelleDe = (liste: Entree[] | undefined, code: string | null | undefined) => (code ? liste?.find((x) => x.code === code)?.libelle || code : '');

/* ── Un choix dans une liste FERMÉE, avec propositions pendant la frappe ── */

const cle = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Champ de référentiel : on tape « gyneco », l'écran propose
 * « Gynécologie-obstétrique ». Aucune saisie libre n'est enregistrée : seul un
 * code de la liste sort (le moteur refuserait le reste).
 */
export function ChampReferentiel({ id, label, liste, valeur, onChange, obligatoire, aide, desactive }: {
  id: string; label: string; liste: Entree[]; valeur: string; onChange: (code: string) => void; obligatoire?: boolean; aide?: string; desactive?: boolean;
}) {
  const choisi = liste.find((x) => x.code === valeur);
  const [texte, setTexte] = useState(choisi?.libelle || '');
  const [ouvert, setOuvert] = useState(false);
  /* La valeur change de l'extérieur (modification, réinitialisation) : le texte
     suit, ajusté pendant le rendu plutôt que par un effet. */
  const [libelleVu, setLibelleVu] = useState(choisi?.libelle || '');
  if ((choisi?.libelle || '') !== libelleVu) { setLibelleVu(choisi?.libelle || ''); if (choisi) setTexte(choisi.libelle); }
  const propositions = useMemo(() => {
    const k = cle(texte);
    if (!k || (choisi && choisi.libelle === texte)) return liste.filter((x) => x.actif !== false).slice(0, 8);
    return liste.filter((x) => x.actif !== false && [x.libelle, ...(x.synonymes || [])].some((s) => cle(s).includes(k) || cle(s).split(' ').some((m) => m.startsWith(k)))).slice(0, 8);
  }, [texte, liste, choisi]);
  const libre = texte.trim() !== '' && !choisi;
  return (
    <div style={{ position: 'relative' }}>
      <label style={lbl} htmlFor={id}>{label}{obligatoire ? ' *' : ''}</label>
      <input id={id} value={texte} disabled={desactive} autoComplete="off" role="combobox" aria-expanded={ouvert} aria-controls={`${id}-liste`}
        onFocus={() => setOuvert(true)} onBlur={() => setTimeout(() => setOuvert(false), 150)}
        onChange={(e) => { setTexte(e.target.value); setOuvert(true); if (valeur) onChange(''); }}
        style={{ ...inputStyle, borderColor: libre ? 'var(--amber-500)' : undefined }} />
      {ouvert && propositions.length > 0 && (
        <ul id={`${id}-liste`} role="listbox" style={{ position: 'absolute', zIndex: 30, insetInlineStart: 0, insetInlineEnd: 0, margin: '4px 0 0', padding: 4, listStyle: 'none',
          background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', maxHeight: 260, overflowY: 'auto' }}>
          {propositions.map((x) => (
            <li key={x.code} role="option" aria-selected={x.code === valeur}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(x.code); setTexte(x.libelle); setOuvert(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'start', padding: '7px 10px', border: 0, background: x.code === valeur ? 'var(--cyan-50)' : 'transparent', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--text-strong)', font: 'inherit' }}>
                {x.libelle}
              </button>
            </li>
          ))}
        </ul>
      )}
      {libre && <div style={{ fontSize: 12, color: 'var(--amber-700)', marginTop: 3 }}>Choisir dans la liste : pas de saisie libre.</div>}
      {aide && !libre && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{aide}</div>}
    </div>
  );
}

/* ── Choisir une fiche existante (organisation ou personne) ─────────────── */

type Trouve = { id: string; entite: string; libelle: string; type?: string; categorie?: string; ville?: string | null; specialite?: string | null; rattachee?: { raison_sociale: string } | null };

export function ChoixFiche({ id, label, entite, types, valeur, libelleInitial, onChange, obligatoire, aide, exclure, sansLibelle, desactive, styleChamp }: {
  id: string; label: string; entite: 'organisation' | 'personne'; types?: string[]; valeur: string; libelleInitial?: string;
  onChange: (id: string, libelle: string) => void; obligatoire?: boolean; aide?: string; exclure?: string;
  /** Le libellé est déjà posé à côté (ligne de saisie de MoMent) : il passe en `aria-label`. */
  sansLibelle?: boolean; desactive?: boolean; styleChamp?: React.CSSProperties;
}) {
  const [q, setQ] = useState(libelleInitial || '');
  const [res, setRes] = useState<Trouve[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState('');
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [initialVu, setInitialVu] = useState(libelleInitial || '');
  if ((libelleInitial || '') !== initialVu) { setInitialVu(libelleInitial || ''); if (libelleInitial) setQ(libelleInitial); }
  const chercher = (texte: string) => {
    if (minuterie.current) clearTimeout(minuterie.current);
    if (texte.trim().length < 2) { setRes([]); return; }
    minuterie.current = setTimeout(() => {
      lire<{ resultats: Trouve[] }>(`ged/annuaire/recherche?q=${encodeURIComponent(texte)}&limite=20`)
        .then((r) => { setErreur(''); setRes(r.resultats.filter((x) => x.entite === entite && x.id !== exclure && (!types?.length || types.includes(String(x.type || x.categorie))))); })
        .catch((e: Error) => setErreur(e.message));
    }, 250);
  };
  return (
    <div style={{ position: 'relative' }}>
      {!sansLibelle && <label style={lbl} htmlFor={id}>{label}{obligatoire ? ' *' : ''}</label>}
      <input id={id} value={q} autoComplete="off" role="combobox" aria-expanded={ouvert} aria-controls={`${id}-liste`} disabled={desactive}
        aria-label={sansLibelle ? label : undefined}
        placeholder={entite === 'organisation' ? 'Nom, téléphone, matricule fiscal…' : 'NOM, prénom, téléphone, CNAM…'}
        onFocus={() => setOuvert(true)} onBlur={() => setTimeout(() => setOuvert(false), 150)}
        onChange={(e) => { setQ(e.target.value); setOuvert(true); chercher(e.target.value); if (valeur) onChange('', ''); }}
        style={{ ...inputStyle, ...styleChamp, borderColor: q && !valeur ? 'var(--amber-500)' : undefined }} />
      {ouvert && res.length > 0 && (
        <ul id={`${id}-liste`} role="listbox" style={{ position: 'absolute', zIndex: 30, insetInlineStart: 0, insetInlineEnd: 0, margin: '4px 0 0', padding: 4, listStyle: 'none',
          background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', maxHeight: 280, overflowY: 'auto' }}>
          {res.map((x) => (
            <li key={x.id} role="option" aria-selected={x.id === valeur}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(x.id, x.libelle); setQ(x.libelle); setOuvert(false); }}
                style={{ display: 'grid', width: '100%', textAlign: 'start', padding: '7px 10px', border: 0, background: 'transparent', borderRadius: 'var(--radius-sm)', cursor: 'pointer', font: 'inherit' }}>
                <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{x.libelle}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{[x.specialite, x.ville, x.rattachee ? `rattachée à ${x.rattachee.raison_sociale}` : null, x.id].filter(Boolean).join(' · ')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {erreur && <div style={{ fontSize: 12, color: 'var(--red-600)', marginTop: 3 }}>{erreur}</div>}
      {aide && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{aide}</div>}
    </div>
  );
}

/* ── Téléphones et emails (sans limite de nombre, indicatif séparé) ─────── */

const INDICATIFS = INDICATIFS_TEL.map((x) => ({ code: x.code, libelle: `${x.pays} ${x.code}` }));
/* Le type se DÉDUIT du numéro ; on ne le force que vers fax, standard ou ligne directe.
   La première option DIT ce qui est déduit (Ines, 20/10/2026) : « Mobile (d’après le numéro) ». */
const TYPES_FORCABLES = [
  { code: 'fax', libelle: 'Fax' }, { code: 'standard', libelle: 'Standard' }, { code: 'ligne_directe', libelle: 'Ligne directe' },
];
const libelleDeduit = (t: { indicatif: string; numero: string }) => {
  const d = typeDeduitTelephone(t.indicatif, t.numero);
  return d ? `${LIBELLE_TYPE_TELEPHONE[d]} (d’après le numéro)` : 'Selon le numéro';
};
const USAGES = [{ code: 'qualite', libelle: 'Qualité' }, { code: 'resultats', libelle: 'Résultats' }, { code: 'facturation', libelle: 'Facturation' }];

export function Coordonnees({ prefixe, tels, emails, onTels, onEmails, avecUsages }: {
  prefixe: string; tels: Tel[]; emails: Mail[]; onTels: (t: Tel[]) => void; onEmails: (e: Mail[]) => void; avecUsages?: boolean;
}) {
  const majTel = (i: number, p: Partial<Tel>) => onTels(tels.map((t, j) => (j === i ? { ...t, ...p } : p.principal ? { ...t, principal: false } : t)));
  const majMail = (i: number, p: Partial<Mail>) => onEmails(emails.map((m, j) => (j === i ? { ...m, ...p } : p.principal ? { ...m, principal: false } : m)));
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 8 }}>
        <legend style={{ ...lbl, marginBottom: 6 }}>Téléphones</legend>
        {tels.map((t, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(130px, 170px) 1fr minmax(120px, 150px) auto auto', gap: 8, alignItems: 'center' }}>
            <Selecteur id={`${prefixe}-ind-${i}`} aria-label={`Indicatif du numéro ${i + 1}`} value={t.indicatif} onChange={(v) => majTel(i, { indicatif: v })} style={inputStyle}>
              {INDICATIFS.map((x) => <option key={x.code} value={x.code}>{x.libelle}</option>)}
            </Selecteur>
            <input aria-label={`Numéro ${i + 1}`} value={t.numero} inputMode="tel" placeholder={t.indicatif === '+216' ? '98 433 054' : 'Numéro national'}
              onChange={(e) => majTel(i, { numero: e.target.value })} style={inputStyle} />
            <Selecteur id={`${prefixe}-type-${i}`} aria-label={`Type du numéro ${i + 1}`} value={['fax', 'standard', 'ligne_directe'].includes(t.type) ? t.type : ''} onChange={(v) => majTel(i, { type: v })} style={inputStyle}>
              <option value="">{libelleDeduit(t)}</option>
              {TYPES_FORCABLES.map((x) => <option key={x.code} value={x.code}>{x.libelle}</option>)}
            </Selecteur>
            <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="radio" name={`${prefixe}-tel-principal`} checked={t.principal} onChange={() => majTel(i, { principal: true })} />Principal
            </label>
            <Bouton taille="sm" variante="contour" aria-label={`Retirer le numéro ${i + 1}`} onClick={() => onTels(tels.filter((_, j) => j !== i))}>Retirer</Bouton>
          </div>
        ))}
        <div><Bouton taille="sm" variante="discret" onClick={() => onTels([...tels, { indicatif: '+216', numero: '', type: '', principal: tels.length === 0 }])}><I n="plus" s={12} />Ajouter un numéro</Bouton></div>
      </fieldset>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 8 }}>
        <legend style={{ ...lbl, marginBottom: 6 }}>Emails <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(facultatifs)</span></legend>
        {emails.map((m, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: avecUsages ? '1fr auto auto auto' : '1fr auto auto', gap: 8, alignItems: 'center' }}>
            <input aria-label={`Email ${i + 1}`} type="email" value={m.adresse} placeholder="contact@exemple.tn" onChange={(e) => majMail(i, { adresse: e.target.value })} style={inputStyle} />
            {avecUsages && (
              <span style={{ display: 'flex', gap: 6 }} role="group" aria-label={`Usages de l’email ${i + 1}`}>
                {USAGES.map((u) => {
                  const actif = (m.usages || []).includes(u.code);
                  return <Bouton key={u.code} taille="sm" variante={actif ? 'primaire' : 'contour'} aria-pressed={actif}
                    onClick={() => majMail(i, { usages: actif ? (m.usages || []).filter((x) => x !== u.code) : [...(m.usages || []), u.code] })}>{u.libelle}</Bouton>;
                })}
              </span>
            )}
            <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="radio" name={`${prefixe}-mail-principal`} checked={m.principal} onChange={() => majMail(i, { principal: true })} />Principal
            </label>
            <Bouton taille="sm" variante="contour" aria-label={`Retirer l’email ${i + 1}`} onClick={() => onEmails(emails.filter((_, j) => j !== i))}>Retirer</Bouton>
          </div>
        ))}
        <div><Bouton taille="sm" variante="discret" onClick={() => onEmails([...emails, { adresse: '', usages: [], principal: emails.length === 0 }])}><I n="plus" s={12} />Ajouter un email</Bouton></div>
      </fieldset>
    </div>
  );
}

/* ── Le verdict du moteur, lisible ──────────────────────────────────────── */

export function VerdictSaisie({ verdict, libelleChamp }: { verdict: Partial<Verdict> | null; libelleChamp: (c: string) => string }) {
  if (!verdict) return null;
  const lignes = [
    ...(verdict.erreurs || []).map((d) => ({ ton: 'danger' as const, d })),
    ...(verdict.avertissements || []).map((d) => ({ ton: 'warning' as const, d })),
    ...(verdict.propositions || []).filter((d) => d.code !== 'inversion_probable').map((d) => ({ ton: 'info' as const, d })),
  ];
  if (!lignes.length) return null;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }} aria-live="polite">
      {lignes.map(({ ton, d }, i) => (
        <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, color: 'var(--text-body)' }}>
          <Pastille ton={ton} compacte>{libelleChamp(d.champ)}</Pastille>
          <span>{d.message}{d.valeur ? <> — « {d.valeur} »</> : null}{d.exemple ? <span style={{ color: 'var(--text-muted)' }}> Exemple : {d.exemple}.</span> : null}</span>
        </li>
      ))}
    </ul>
  );
}

/* ── « Doublon probable » (§5.1) ────────────────────────────────────────── */

/**
 * La modale des doublons : « Ouvrir la fiche existante » ou « Créer quand
 * même », avec un motif obligatoire (tracé au journal). Un doublon BLOQUANT
 * (même CNAM, même MF) ne propose que l'ouverture. Le cas « même mobile chez
 * un autre fournisseur » propose une affiliation au lieu d'une fiche.
 */
export function ModaleDoublons({ code, candidats, onOuvrir, onForcer, onAffilier, onClose, enCours, libelleForcer = 'Créer quand même' }: {
  code: string; candidats: Candidat[]; onOuvrir: (id: string) => void; onForcer: (motif: string) => void;
  onAffilier?: (id: string) => void; onClose: () => void; enCours?: boolean;
  /** « Créer quand même » à la création, « Enregistrer quand même » en modification. */
  libelleForcer?: string;
}) {
  const [motif, setMotif] = useState('');
  const bloquant = code === 'doublon_bloquant';
  const affiliation = code === 'proposer_affiliation';
  return (
    <Modale titre={bloquant ? 'Cette fiche existe déjà' : affiliation ? 'Cette personne existe déjà' : 'Doublon probable'} onClose={onClose} largeur={620}
      sousTitre={bloquant ? 'Même code CNAM ou même matricule fiscal : une seconde fiche n’est pas possible.'
        : affiliation ? 'Même mobile et même prénom chez un autre fournisseur : ajoutez-lui plutôt une affiliation.'
          : 'Vérifiez : si c’est la même personne, ouvrez la fiche existante et fusionnez (Admin qualité).'}
      pied={bloquant || affiliation
        ? <PiedActions taille="sm" annuler={{ libelle: 'Fermer', onClick: onClose }} />
        : <PiedActions taille="sm" annuler={{ onClick: onClose }} principale={{ libelle: libelleForcer, variante: 'primaire', chargement: enCours, disabled: motif.trim().length < 10 || enCours, onClick: () => onForcer(motif.trim()) }} />}>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
        {candidats.map((c) => (
          <li key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)' }}>
            <span style={{ display: 'grid', gap: 2 }}>
              <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{c.libelle} <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 400, color: 'var(--text-muted)' }}>{c.id}</span></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.motifs.map((m) => m.libelle).join(' · ')}</span>
            </span>
            <span style={{ display: 'flex', gap: 6 }}>
              {affiliation && onAffilier && <Bouton taille="sm" variante="primaire" onClick={() => onAffilier(c.id)}>Ajouter une affiliation</Bouton>}
              <Bouton taille="sm" variante="contour" onClick={() => onOuvrir(c.id)}>Ouvrir la fiche existante</Bouton>
            </span>
          </li>
        ))}
      </ul>
      {!bloquant && !affiliation && (
        <div style={{ marginTop: 14 }}>
          <label style={lbl} htmlFor="doublon-motif">Motif pour « {libelleForcer.toLowerCase()} » *</label>
          <textarea id="doublon-motif" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. homonyme, autre cabinet, frère du Dr…"
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>10 caractères au moins — le motif est conservé au journal de la fiche.</div>
        </div>
      )}
    </Modale>
  );
}

/** Téléphones d'une fiche servie → lignes éditables. */
export const telsEditables = (t: Tel[] = []) => t.filter((x) => x.actif !== false).map((x) => ({ indicatif: x.indicatif, numero: x.numero_national || x.numero, type: x.type, principal: x.principal }));
export const mailsEditables = (m: Mail[] = []) => m.filter((x) => x.actif !== false).map((x) => ({ adresse: x.adresse, usages: (x.usages || []).filter((u) => u !== 'general'), principal: x.principal }));
